import React from 'react';
import { Button, Space, Switch, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, HistoryOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { formatDate, formatFullDate } from '../../utils/dateUtils';
import type { SyncTask, SyncTaskRun } from './types';
import {
  notificationStatusColors,
  notificationStatusLabels,
  runStatusColors,
  runStatusLabels,
  triggerLabels,
} from './statusMeta';

type TaskHandlers = {
  onToggleEnabled: (task: SyncTask, enabled: boolean) => void;
  onEdit: (task: SyncTask) => void;
  onRun: (task: SyncTask) => void;
  onOpenRuns: (task: SyncTask) => void;
};

export const createTaskColumns = ({
  onToggleEnabled,
  onEdit,
  onRun,
  onOpenRuns,
}: TaskHandlers): ColumnsType<SyncTask> => [
  { title: '任务名称', dataIndex: 'taskName', width: 180 },
  {
    title: '平台',
    dataIndex: 'platform',
    width: 80,
    render: (value: string) => <Tag color={value === '172' ? 'orange' : 'blue'}>{value === '172' ? '172' : value}</Tag>,
  },
  {
    title: '启用',
    dataIndex: 'enabled',
    width: 90,
    render: (enabled: boolean, record) => <Switch checked={enabled} onChange={(checked) => onToggleEnabled(record, checked)} />,
  },
  { title: '频率', dataIndex: 'intervalMinutes', width: 100, render: (value: number) => `${value} 分钟` },
  { title: '回看天数', dataIndex: 'lookbackDays', width: 100, render: (value: number) => `${value} 天` },
  { title: '批量上限', dataIndex: 'batchLimit', width: 100, render: (value: number) => `${value} 单` },
  {
    title: '最大重提',
    dataIndex: 'maxRetryCount',
    width: 100,
    render: (value: number | undefined, record) => record.taskType === 'order_auto_retry' ? `${value || 0} 次` : '-',
  },
  {
    title: '最近结果',
    dataIndex: 'lastRunStatus',
    width: 120,
    render: (value: string) => <Tag color={runStatusColors[value] || 'default'}>{runStatusLabels[value] || value || '未执行'}</Tag>,
  },
  { title: '最近处理', dataIndex: 'lastRunCount', width: 90, render: (value?: number) => value ?? 0 },
  {
    title: '连续失败',
    dataIndex: 'failureStreak',
    width: 90,
    render: (value?: number) => <Tag color={(value || 0) >= 3 ? 'red' : (value || 0) > 0 ? 'orange' : 'default'}>{value || 0}</Tag>,
  },
  { title: '最近通知', dataIndex: 'lastFailureNotifiedAt', width: 150, render: (value?: string) => formatDate(value) },
  { title: '最近开始', dataIndex: 'lastRunStartedAt', width: 150, render: (value?: string) => formatDate(value) },
  { title: '最近完成', dataIndex: 'lastRunFinishedAt', width: 150, render: (value?: string) => formatDate(value) },
  { title: '结果消息', dataIndex: 'lastRunMessage', ellipsis: true, render: (value?: string) => value || '-' },
  {
    title: '操作',
    key: 'actions',
    width: 180,
    fixed: 'right',
    render: (_, record) => (
      <Space size="small">
        <Button icon={<EditOutlined />} onClick={() => onEdit(record)}>编辑</Button>
        <Button type="primary" ghost icon={<PlayCircleOutlined />} onClick={() => onRun(record)} disabled={record.lastRunStatus === 'running'}>立即执行</Button>
        <Button icon={<HistoryOutlined />} onClick={() => onOpenRuns(record)}>记录</Button>
      </Space>
    ),
  },
];

export const createRunColumns = (): ColumnsType<SyncTaskRun> => [
  { title: 'ID', dataIndex: 'id', width: 80 },
  { title: '触发方式', dataIndex: 'triggerType', width: 100, render: (value: string) => triggerLabels[value] || value },
  {
    title: '状态',
    dataIndex: 'status',
    width: 100,
    render: (value: string) => <Tag color={runStatusColors[value] || 'default'}>{runStatusLabels[value] || value}</Tag>,
  },
  { title: '开始时间', dataIndex: 'startedAt', width: 160, render: (value?: string) => formatFullDate(value) },
  { title: '结束时间', dataIndex: 'finishedAt', width: 160, render: (value?: string) => formatFullDate(value) },
  { title: '处理', dataIndex: 'processedCount', width: 80 },
  { title: '成功', dataIndex: 'successCount', width: 80 },
  { title: '失败', dataIndex: 'failedCount', width: 80 },
  {
    title: '通知',
    dataIndex: 'notificationStatus',
    width: 110,
    render: (value?: string) => value ? <Tag color={notificationStatusColors[value] || 'default'}>{notificationStatusLabels[value] || value}</Tag> : '-',
  },
  { title: '通知时间', dataIndex: 'notifiedAt', width: 160, render: (value?: string) => formatFullDate(value) },
  { title: '消息', dataIndex: 'message', ellipsis: true },
];
